import { isSupportedCountry, isValidPhoneNumber } from 'libphonenumber-js';
import { useIntl } from 'react-intl';

// TODO: consider using the /\S+@\S+\.\S{2,}/ pattern instead
export const emailValidator = {
  required: true,
  pattern: /^\S+@\S+\.\S+$/,
};

export const passwordValidator = {
  required: true,
};

export const termsValidator = {
  required: true,
};

export const setPasswordValidator = {
  minLength: 8,
  maxLength: 50,
  required: true,
  pattern: /\d/,
};

/**
 * Length bounds for a single name field (a first name or a last name).
 *
 * The character rule deliberately does NOT live here. It is the client mirror of
 * the backend Latin-script policy (MP-6863) and lives in
 * `@/utility/nameValidation` alongside the ported api fixture table that pins it —
 * a second copy of the pattern in this file is exactly the drift MP-6864 removes.
 * Consumed via `useStrictNameInputRules` (profile edit, checkout) and
 * `useFullNameInputRules` (signup, guest invites).
 */
export const strictNameValidator = {
  minLength: 2,
  maxLength: 40,
};

/**
 * Bounds for the blocking romanized-name gate at checkout (MP-6866).
 *
 * That gate can't be dismissed, so a client rule the guest can't satisfy locks
 * them out of booking entirely. Its bounds therefore follow the server rather
 * than `strictNameValidator`'s client-only ones: the API has no minimum beyond
 * required (a romanized surname can be one letter — 오 is "O"), and on write it
 * truncates each name to 60 UTF-8 bytes (Profile.NormalizeNames) rather than
 * rejecting it. The cap is in bytes, not characters, because that is what the
 * server cuts on: every non-ASCII Latin letter is 2–3 bytes, so a 40-character
 * Vietnamese name can be 70. Checking bytes asks the guest to shorten a long
 * name themselves instead of having it silently cut, possibly mid-character.
 */
export const romanizedNameValidator = {
  minLength: 1,
  maxBytes: 60,
};

/**
 * Length bounds for a combined "Full name" field, which holds first + last.
 *
 * Bounds only — required-ness is supplied by the rule builders in
 * `useInputRules`, not read from here.
 */
export const fullNameValidator = {
  minLength: 2,
  maxLength: 100,
};

type TPhoneValidatorOptions = {
  /**
   * The country the API would read an existing number against, i.e. the country on the
   * user's profile address. Numbers captured before E.164 carry no country of their own,
   * so this is what keeps a saved profile submittable without retyping the phone. Falls
   * back to US, exactly as the API does when the profile carries no country.
   */
  interpretCountry?: string;
};

/**
 * react-hook-form rules for fields backed by PhoneNumberInput, which always submits E.164.
 *
 * Validates with the same libphonenumber ruleset the API enforces, so fake numbers (555
 * area codes and the like) fail here instead of coming back as an InvalidFields rejection.
 */
export const usePhoneValidatorBuilder = () => {
  const intl = useIntl();

  const required = intl.formatMessage({
    id: 'Tge6sA',
    defaultMessage: 'Phone number is required.',
    description: 'Phone number field > required error',
  });

  const invalid = intl.formatMessage({
    id: '/VrVlR',
    defaultMessage: 'Please enter a valid phone number.',
    description: 'Phone number field > invalid number error',
  });

  return ({ interpretCountry }: TPhoneValidatorOptions = {}) => ({
    required,
    validate: (value?: string) => {
      if (!value) {
        // Emptiness is the `required` rule's job.
        return true;
      }

      if (isValidPhoneNumber(value)) {
        return true;
      }

      // A value with no country of its own gets read the way the API reads it, which
      // means defaulting to US when the profile has no country — otherwise a legacy
      // stored number would block the whole form, fields unrelated to the phone included.
      const country = (interpretCountry || 'US').toUpperCase();

      if (isSupportedCountry(country) && isValidPhoneNumber(value, country)) {
        return true;
      }

      return invalid;
    },
  });
};

export const addressValidator = {
  /**
   * This pattern is very permissive, and could be used for city, address.
   * It must be a combination of words delimited by space, dot, comma or dash, and
   * might also use apostrophe.
   * It might start/end with some spaces.
   */
  pattern: /^ *[a-zA-ZÀ-ÖØ-öø-ɏ0-9]+((\.|,|-|'| )[a-zA-ZÀ-ÖØ-öø-ɏ0-9]*)* *$/,
  required: true,
  minLength: 2,
  maxLength: 100,
};

export const zipCodeValidator = {
  /**
   * There isn't really a way to validate all zip codes with a regex, but we are good
   * to inherit the address and allow the same special characters.
   */
  pattern: addressValidator.pattern,
  required: true,
  minLength: 2,
  maxLength: 30,
};
